import { CropDefaultStyle, CroppingStyle, CropStyle } from "../crops";
import { CropPurpose, CropStyleFillPattern, GrassCuts, GrassManagement } from "../generated/enums";

export class CroppingStyleLookup {
    constructor(private readonly styles: CropStyle[]) {
        this.styles = (styles ?? [])
            .filter((x) => Boolean(x.Color)) // There are some (old, unsupported) records in the database where `{ color: null, pattern: any }`. Ignore these records.
            .map((style) => ({
                ...style,
                Pattern: style.Pattern || CropStyleFillPattern.None,
            }));
    }

    private static readonly forYear: (year: number) => (value: CropStyle) => boolean = (year) => (value) =>
        value.Year === year;

    private static readonly forCrop: (cropId: number) => (value: CropStyle) => boolean = (cropId) => (x) =>
        x.CropId === cropId && !x.VarietyId && !x.Purpose && !x.GrassManagement && !x.GrassCuts && !x.CroppingCropDescr;

    private static readonly forVariety: (cropId: number, varietyId: number) => (value: CropStyle) => boolean =
        (cropId, varietyId) => (x) =>
            x.CropId === cropId &&
            !!x.VarietyId &&
            x.VarietyId === varietyId &&
            !x.Purpose &&
            !x.GrassManagement &&
            !x.GrassCuts &&
            !x.CroppingCropDescr;

    private static readonly forDescription: (cropId: number, description: string) => (value: CropStyle) => boolean =
        (cropId, description) => (x) =>
            x.CropId === cropId &&
            !x.VarietyId &&
            !x.Purpose &&
            !x.GrassManagement &&
            !x.GrassCuts &&
            x.CroppingCropDescr === description;

    private static readonly forPurpose: (cropId: number, purpose: CropPurpose) => (value: CropStyle) => boolean =
        (cropId, purpose) => (x) =>
            x.CropId === cropId &&
            !x.VarietyId &&
            !!x.Purpose &&
            x.Purpose === purpose &&
            !x.GrassManagement &&
            !x.GrassCuts &&
            !x.CroppingCropDescr;

    private static readonly forGrass: (
        cropId: number,
        grassManagement: GrassManagement,
        grassCuts: GrassCuts | null | undefined
    ) => (value: CropStyle) => boolean = (cropId, grassManagement, grassCuts) => (x) =>
        x.CropId === cropId &&
        !x.VarietyId &&
        !x.Purpose &&
        !!x.GrassManagement &&
        x.GrassManagement === grassManagement &&
        // { grassManagement: not null, grassCuts: null } is a possibility.
        (!!grassCuts ? x.GrassCuts === grassCuts : x.GrassCuts === undefined || x.GrassCuts === null) &&
        !x.CroppingCropDescr;

    /**
     * Finds a crop-level CropStyle for the specified `year` and `cropId` (or undefined).
     */
    public findForCrop(year: number, cropId: number): CropStyle | undefined {
        return this.styles.filter(CroppingStyleLookup.forYear(year)).find(CroppingStyleLookup.forCrop(cropId));
    }

    /**
     * Finds a variety-level CropStyle for the specified `year`, `cropId` & `varietyId` (or undefined).
     */
    public findForVariety(year: number, cropId: number, varietyId: number): CropStyle | undefined {
        return this.styles
            .filter(CroppingStyleLookup.forYear(year))
            .find(CroppingStyleLookup.forVariety(cropId, varietyId));
    }

    /**
     * Finds a description-level CropStyle for the specified `year`, `cropId` & `description` (or undefined).
     */
    public findForDescription(year: number, cropId: number, description: string): CropStyle | undefined {
        return this.styles
            .filter(CroppingStyleLookup.forYear(year))
            .find(CroppingStyleLookup.forDescription(cropId, description));
    }

    /**
     * Finds a description-level CropStyle for the specified `year`, `cropId` & `description` (or undefined).
     */
    public findForPurpose(year: number, cropId: number, purpose: CropPurpose): CropStyle | undefined {
        return this.styles
            .filter(CroppingStyleLookup.forYear(year))
            .find(CroppingStyleLookup.forPurpose(cropId, purpose));
    }

    public findForGrass(
        year: number,
        cropId: number,
        grassManagement: GrassManagement,
        grassCuts: GrassCuts | null | undefined
    ): CropStyle | undefined {
        return this.styles
            .filter(CroppingStyleLookup.forYear(year))
            .find(CroppingStyleLookup.forGrass(cropId, grassManagement, grassCuts));
    }

    getCroppingStyle(args: ProviderInput): CroppingStyle {
        const getDefault = (def: CropDefaultStyle, varietyId: number | null) => {
            if (!def) {
                return null;
            }
            return {
                CropId: args.cropId,
                VarietyId: varietyId,
                Purpose: null,
                Year: args.year,
                Color: def.Colour,
                Pattern: def.Pattern || CropStyleFillPattern.None,
            };
        };

        let crop: CropStyle = this.findForCrop(args.year, args.cropId);
        let variety: CropStyle = args.varietyId
            ? this.findForVariety(args.year, args.cropId, args.varietyId)
            : undefined;

        // Note: the default variety style is only used if
        // there was no custom style set

        if (!crop) {
            crop = getDefault(args.crop?.style, null);
            variety = variety || getDefault(args.variety?.style, args.varietyId);
        }

        // If cropping has a `description`, we can determine a `description` style.
        // Fallback to `crop`
        const description =
            (args.croppingCropDescr ? this.findForDescription(args.year, args.cropId, args.croppingCropDescr) : null) ??
            crop;

        // If cropping has either grass or purpose, we can determine a `purposeAndGrass` style.
        // If both are defined, `grass` style takes precedence over `purpose` style.
        // Fallback to `crop`
        const grass = args.grassManagement
            ? this.findForGrass(args.year, args.cropId, args.grassManagement, args.grassCuts)
            : null;
        const purposeAndGrass =
            grass ?? (args.purpose ? this.findForPurpose(args.year, args.cropId, args.purpose) : null) ?? crop;

        return {
            crop,
            variety,
            description,
            purposeAndGrass,
        };
    }
}

interface ProviderInput {
    year: number;
    cropId: number;
    crop?: { style?: CropDefaultStyle };
    varietyId?: number;
    variety?: { style?: CropDefaultStyle };
    purpose?: CropPurpose;
    grassManagement?: GrassManagement;
    grassCuts?: GrassCuts;
    croppingCropDescr?: string;
}

export type CroppingStyleProvider = (arg: ProviderInput) => CroppingStyle;

export function createCroppingStyleProvider(styles: CropStyle[]): CroppingStyleProvider {
    const lookup = new CroppingStyleLookup(styles);
    return (args: ProviderInput) => lookup.getCroppingStyle(args);
}
